import { IDocumentAssemblerGenerationPlaceholder } from '@commenda/api';
import {
  Badge,
  ColumnGroup,
  DataTable,
  EmptyState,
  TruncatedText,
} from '@commenda/kakashi';

import { DocumentAssemblerHelper } from './DocumentAssemblerHelper';

interface DocumentAssemblerGenerationPlaceholdersTableProps {
  placeholders: IDocumentAssemblerGenerationPlaceholder[];
}

const WrittenValue = ({
  placeholder,
}: {
  placeholder: IDocumentAssemblerGenerationPlaceholder;
}) => {
  if (placeholder.value === null) {
    return <span className="text-text-tertiary">—</span>;
  }
  if (placeholder.value === '') {
    return <span className="text-text-tertiary italic">written as empty</span>;
  }

  return (
    <TruncatedText className="txt-small-regular text-text-primary">
      {placeholder.value}
    </TruncatedText>
  );
};

export const DocumentAssemblerGenerationPlaceholdersTable = ({
  placeholders,
}: DocumentAssemblerGenerationPlaceholdersTableProps) => {
  if (placeholders.length === 0) {
    return (
      <EmptyState
        heading="This template asked for no values"
        description="Its version declares no placeholders."
      />
    );
  }

  const columnGroups: ColumnGroup<
    IDocumentAssemblerGenerationPlaceholder,
    unknown
  >[] = [
    {
      id: 'slot',
      sticky: 'left',
      minWidth: 240,
      columns: [
        {
          id: 'ordinal',
          accessorFn: (placeholder) => placeholder.ordinal,
          title: '#',
          header: '#',
          width: 56,
          cell: ({ row }) => (
            <span className="txt-extra-small-regular text-text-tertiary">
              {row.original.ordinal + 1}
            </span>
          ),
        },
        {
          id: 'state',
          accessorFn: (placeholder) =>
            DocumentAssemblerHelper.getPlaceholderAnswerBadge(placeholder)
              .label,
          title: 'State',
          header: 'State',
          width: 184,
          cell: ({ row }) => {
            const badge = DocumentAssemblerHelper.getPlaceholderAnswerBadge(
              row.original
            );

            return (
              <Badge variant={badge.variant} size="sm">
                {badge.label}
              </Badge>
            );
          },
        },
      ],
    },
    {
      id: 'written',
      minWidth: 320,
      columns: [
        {
          id: 'value',
          accessorFn: (placeholder) => placeholder.value ?? '',
          title: 'What was written',
          header: 'What was written',
          width: 400,
          cell: ({ row }) => (
            <div className="txt-small-regular text-text-primary">
              <WrittenValue placeholder={row.original} />
            </div>
          ),
        },
      ],
    },
    {
      id: 'asked',
      minWidth: 320,
      columns: [
        {
          id: 'instruction',
          accessorFn: (placeholder) => placeholder.instruction,
          title: 'The instruction that asked for it',
          header: 'The instruction that asked for it',
          cell: ({ row }) => (
            <TruncatedText className="txt-small-regular text-text-secondary">
              {row.original.instruction}
            </TruncatedText>
          ),
        },
      ],
    },
  ];

  return (
    <DataTable
      columnGroups={columnGroups}
      data={placeholders}
      searchConfig={{
        isEnabled: true,
        columnsToEnableSearchFor: ['value', 'instruction'],
        placeholder: 'Search the instructions and the values',
        debounceMs: 200,
      }}
      expandableRowConfig={{
        isEnabled: true,
        getRowId: (row) => (row as IDocumentAssemblerGenerationPlaceholder).id,
        renderExpandedContent: (row) => {
          const { prefix, tag, suffix } = (
            row as IDocumentAssemblerGenerationPlaceholder
          ).placeholdersSurroundingContext;

          return (
            <div className="flex flex-col gap-xs p-lg">
              <span className="txt-extra-small-medium text-text-tertiary">
                Where it sits in the document
              </span>
              <p className="txt-small-regular whitespace-pre-wrap text-text-secondary">
                {prefix}
                <span className="txt-small-medium text-text-primary">
                  {tag}
                </span>
                {suffix}
              </p>
            </div>
          );
        },
        expandColumn: { sticky: 'left' },
      }}
      rowClassName={(placeholder) =>
        DocumentAssemblerHelper.isLeftOut(placeholder)
          ? 'text-text-tertiary'
          : ''
      }
    />
  );
};
